/**************************************************************************************************/
/** @export Class @name TypesHelper                                                             **/
/**************************************************************************************************/
export class TypesHelper
{
	/*************************************************/
	/** @Methods                                   **/
	/*************************************************/
	public static getArrayFromStackByProperty<T, S>(arr: Array<T>, stack: Array<S>, property: string) : Array<T>
	{
		if (!this.isArrayInvalidOrEmpty<T>(arr) && !this.isArrayInvalidOrEmpty<S>(stack))
		{
			const aux = [] as Array<T>
			for (const e of stack) aux.push(arr.find((x: any) => x[property] === e) as T)
			return aux
		}
		return []
	}

	public static getArrayLastElement<T>(arr: Array<T>) : T
	{
		return arr[arr.length - 1]
	}

	public static getDateAsNumbersOnly(date?: Date) : string
	{
		const _date     = date || new Date()
		const isoString = _date.toISOString()
		return isoString.replace(/[\-]|[T]|[:]|[\.].+/g, '')
	}

	public static getDateAsStringDate(date: Date) : string
	{
		const day   = date.getDate().toString().padStart(2, '0')
		const month = (date.getMonth() + 1).toString().padStart(2, '0')
		const year  = date.getFullYear().toString()
		return `${ day }-${ month }-${ year }`.replace(/^\-/, '').replace(/\-$/, '')
	}

	public static getDateDayOfTheYear(date: Date) : number
	{
		const dayCount = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334]
		const mn = date.getMonth()
		const dn = date.getDate()
		let dayOfYear = dayCount[mn] + dn
		if (mn > 1 && this.isDateLeapYear(date)) dayOfYear++
		return dayOfYear
	}

	public static getDateLatestCorrelativeDays(asDateOrString: 'String' | 'Date', numDays: number, date = new Date) : Array<any>
	{
		const DAY_IN_MS = 86400 * 1000
		const dates = []

		for (let i = 0; i < numDays; i++)
		{
			const curMS = date.getTime()
			date.setTime(curMS - (i === 0 ? 0 : DAY_IN_MS));
			(asDateOrString === 'Date') ? dates.push(date) : dates.push(this.getDateAsStringDate(date))
		}
		return dates
	}

	public static getDateLatestCorrelativeMonths(asDateOrString: 'String' | 'Date', numMonths: number, date = new Date) : Array<any>
	{
		const dates = []

		for (let i = 0; i < numMonths; i++)
		{
			if (i > 0) date.setDate(0);
			(asDateOrString === 'Date') ? dates.push(date) : dates.push(this.getDateAsStringDate(date))
		}
		return dates
	}

	public static getStringWidthFromCanvasContext(text: string, font?: string) : number
	{
		const canvas = document.createElement("canvas")
		const context = canvas.getContext("2d")!
		if (font) context.font = font
		const metrics = context.measureText(text)
		return metrics.width
	}

	public static isArrayInvalidOrEmpty<T>(arr: Array<T>) : boolean
	{
		if (Array.isArray(arr))
		{
			if (arr.length === 0) return true
			for (const v of arr) if (!v) return true
			return false
		}
		return true
	}

	public static isBooleanValid(b: boolean) : boolean
	{
		return typeof b === 'boolean'
	}

	public static isDateLeapYear(date: Date) : boolean
	{
		const year = date.getFullYear()
		if ((year & 3) != 0) return false
		return ((year % 100) != 0 || (year % 400) == 0)
	}

	public static isDateValid(d: Date) : boolean
	{
		return d instanceof Date && !isNaN(<any> d)
	}

	public static isDateValidAsString(date: string) : boolean
	{
		const isValidDateHourString = /^\d\d\-\d\d\-\d\d\d\d\s\d\d:\d\d$/.test(date)
		const isValidDateString = /^\d\d\-\d\d\-\d\d\d\d$/.test(date)
		return isValidDateHourString || isValidDateString
	}

	public static isStringInvalidOrEmpty(str: string) : boolean
	{
		return str === null || str === undefined || str === ''
	}

	public static isStringValidEmailFormat(email: string) : boolean
	{
		return /^[a-z0-9]+(.+)@[a-z0-9]+\.[a-z0-9]+$/i.test(email)
	}

	public static isStringValidRutFormat(rut: string) : boolean
	{
		if (!rut) return false
		rut = rut.replace(/\./g, '')
		if (!/^[0-9]+[-|‐]{1}[0-9kK]{1}$/.test(rut)) return false

		var tmp  = rut.split('-')
		var digv = tmp[1]
		var _rut = tmp[0] as unknown
		if (digv == 'K') digv = 'k'

		function dv(T: number)
		{
			var M = 0, S = 1

			for (; T; T = Math.floor(T / 10))
			{
				S = (S + T % 10 * (9 - M++ % 6)) % 11
			}
			return S ? S - 1 : 'k'
		}

		return dv(_rut as number) == digv
	}

	public static isValueInArray(value: any, values: Array<any>) : boolean
	{
		for (const v of values)
		{
			if (value === v) return true
		}
		return false
	}

	public static isValueNull(value: any) : boolean
	{
		return value === null
	}

	public static isValueNullOrUndefined(value: any) : boolean
	{
		return this.isValueNull(value) || this.isValueUndefined(value)
	}

	public static isValueUndefined(value: any) : boolean
	{
		return value === undefined
	}

	public static newDate(day?: number, month?: number, year?: number) : Date
	{
		const date = new Date()
		if (day)   date.setDate(day)
		if (month) date.setMonth(month)
		if (year)  date.setFullYear(year)
		return date
	}

	public static newMonth(month: number | null = null, year: number | null = null, includeFirstDayString = false) : string
	{
		const date = new Date()
		date.setDate(1)
		if (month) date.setMonth(month)
		if (year)  date.setFullYear(year)
		return `${ date.getFullYear() }-${ (date.getMonth() + 1).toString().padStart(2, '0') }${ includeFirstDayString ? '-01' : '' }`
	}

	public static newObjectModuleStructure(index: number, selectable: boolean, title: string, iconName: string) : any
	{
		return { _params: { index, selectable, visible: true }, icon: iconName, title }
	}

	public static newObjectWithKeysFromStack<T>(arr: Array<T>) : any
	{
		const obj: any = {}
		for (const e of arr) obj[e] = []
		return obj
	}

	public static onArrayMapping<T>(arr: Array<T>, onTrueCallback: (item: T) => void, onFalse: () => any) : any
	{
		if (Array.isArray(arr) && arr.length > 0) return arr.map(onTrueCallback)
		return onFalse()
	}

	public static toBoolean(b: boolean | string | number) : boolean
	{
		if (typeof b === 'boolean') return b === true   ? true : false
		if (typeof b === 'string')  return b === 'true' ? true : false
		if (typeof b === 'number')  return b === 1      ? true : false
		return false
	}

	public static toDatePartsFromString(date: string, includeSeconds?: boolean, swapYearAndDays?: boolean) : { date: string, time: string } | null
	{
		// Convertir a tipo Date, el string de la Fecha.
		const asDate = new Date(date)
		if (!asDate) return null

		// Obtener los elementos de las Fechas.
		const days = asDate.getDate().toString().padStart(2, '0')
		const months = (asDate.getMonth() + 1).toString().padStart(2, '0')
		const year = asDate.getFullYear()

		// Segun el tercer parametro opcional, cambiar el orden de los días y año.
		const _date = swapYearAndDays ? `${ year }-${ months }-${ days }` : `${ days }-${ months }-${ year }`

		// Obtener los elementos de las Horas.
		const hours = asDate.getHours().toString().padStart(2, '0')
		const minutes = asDate.getMinutes().toString().padStart(2, '0')
		let _time = `${hours}:${minutes}`

		// Los Segundos son opcionales por defecto.
		if (includeSeconds)
		{
			const seconds = asDate.getSeconds().toString().padStart(2, '0')
			_time += `:${seconds}`
		}

		// Retornar objeto co ambas partes de una Fecha.
		return { date: _date, time: _time }
	}

	public static ToDateReversedAsString(sDate: string, separator = '-', newSeparator = '-') : string | null
	{
		if (!sDate) return null
		const split = sDate.split(separator)
		return `${ split[2] }${ newSeparator }${ split[1] }${ newSeparator }${ split[0] }`
	}

	public static toStringBase64Encoded(str: string) : string
	{
		return Buffer.from(str, 'utf8').toString('base64')
	}

	public static toStringCurrencyFormat(value: number | string, asCurrency = true) : string
	{
		const v = typeof value === 'string' ? Number(value) : value
		const s = Intl.NumberFormat('es-CL', { currency: 'CLP', style: 'currency' }).format(v)
		return asCurrency ? s : s.replace('$', '')
	}

	public static toStringLimitedLength(name: string, width = 100, font = '') : string
	{
		let formatted = ''

		for (const c of name)
		{
			if (this.getStringWidthFromCanvasContext(formatted, font) < width)
			{
				formatted += c
			}
			else
			{
				formatted += '...'
				break
			}
		}
		return formatted
	}

	public static toStringReversed(str: string) : string
	{
		const arr = str.split('')
		const rev = arr.reverse()
		return rev.join('')
	}

	public static toStringValidRutFormat(rut: string, dot = '.') : string | null
	{
		if (rut?.length === 0) return null

		// Realizar una Prelimpieza del contenido del Rut, eliminando caracteres no permitidos.
		const parts = []
		let formatted = '', dv = ''

		// Eliminar todos los caracteres no validos.
		rut = rut?.replace(/[^0-9Kk]/g, '')

		if (rut)
		{
			// Separar el ultimo caracter como DV.
			dv = rut[rut.length - 1]
			rut = rut.substring(0, rut.length - 1)

			// Dividir en 'partes' la porción del Rut.
			while (rut.length > 0)
			{
				const section = rut.substring(rut.length - 3)

				if (section !== undefined)
				{
					parts.push(section)
					rut = rut.replace(new RegExp(`${ section }$`), '')
				}
				else
				{
					parts.push(rut)
					rut = ''
				}
			}

			// Finalizar con el formato correspondiente
			for (let i = parts.length; i > 0; i--) formatted += `${ parts[i - 1] }${ dot }`
			formatted = formatted.replace(/\.$/, '')
			formatted += `-${ dv }`
			return formatted.replace(/^\-/, '')
		}

		return ''
	}
}